// Client-side product-usage events for SignalMax's OWN apps (saas, careers). Our apps are React apps, not
// the pasted pixel, so instead of window.smx they call trackProduct()/identifyProduct() directly. Both post
// to the SAME public beacon (/api/web/track) the pixel and WebTracker use, so there is no second pipeline:
// the host routes the event to this app's workspace scope (app = 'ws:<id>'), and loadProductUsage reads it
// exactly like a customer's smx('track', ...) event. Always best-effort and non-blocking.
//
// identity: once the signed-in user is known, call setProductUser(id) (WebTracker does this on every
// authenticated pageview) so every later trackProduct() carries external_user_id and Product Usage counts
// active USERS, not just devices. identifyProduct() additionally sends an identify event to stamp the visitor.

import { supabase } from "@/lib/supabaseClient"

// The current signed-in user's id, remembered in-module so trackProduct() always carries it. Set by
// WebTracker (auto, on authenticated pageviews) and by identifyProduct().
let currentUserId: string | null = null

/** Remember (or clear) the acting user so subsequent product events carry external_user_id. */
export function setProductUser(id: string | null | undefined): void {
  currentUserId = id ? String(id).slice(0, 128) : null
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" }
  try {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (token) headers["Authorization"] = `Bearer ${token}`
  } catch { /* anonymous — the event still records, just unlinked */ }
  return headers
}

async function post(body: Record<string, unknown>): Promise<void> {
  if (typeof window === "undefined") return
  try {
    const headers = await authHeaders()
    void fetch("/api/web/track", {
      method: "POST", headers, credentials: "same-origin", keepalive: true,
      body: JSON.stringify({ path: window.location.pathname, title: document.title || null, ...body }),
    }).catch(() => {})
  } catch { /* best-effort */ }
}

/** Record a product-usage action from one of our apps, e.g. trackProduct('today_opened', { tier }). The
 *  event carries the current user id (if identified) so it counts toward active users. Reserved analytics
 *  types (pageview/page_exit/identify) are handled by WebTracker/identifyProduct, not here. */
export async function trackProduct(event: string, properties?: Record<string, unknown>): Promise<void> {
  const eventType = (event || "").trim().slice(0, 64)
  if (!eventType) return
  await post({
    event_type: eventType,
    properties: properties && typeof properties === "object" ? properties : null,
    external_user_id: currentUserId,
  })
}

/** Name the signed-in user: remember the id for later events and send an identify event so the visitor is
 *  stamped with it (so even events that forget to pass it inherit the identity). Traits are optional. */
export async function identifyProduct(id: string, traits?: Record<string, unknown>): Promise<void> {
  const uid = id ? String(id).slice(0, 128) : ""
  if (!uid) return
  setProductUser(uid)
  await post({
    event_type: "identify",
    properties: traits && typeof traits === "object" ? traits : null,
    external_user_id: uid,
  })
}
